import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

const chartPlaceholderSx = {
  height: 230,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "grey.50",
  backgroundImage:
    "repeating-linear-gradient(to right, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 28px), repeating-linear-gradient(to bottom, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 24px)",
}

export const DashboardWorkloadChart: FC = () => {
  const { t } = useTranslation()
  const [workloadMode, setWorkloadMode] = useState("takeInCharge")
  const [workloadDate, setWorkloadDate] = useState(dayjs().format("YYYY-MM-DD"))

  const workloadDays = useMemo(
    () => [
      t("dashboard.day.monday"),
      t("dashboard.day.tuesday"),
      t("dashboard.day.wednesday"),
      t("dashboard.day.thursday"),
      t("dashboard.day.friday"),
      t("dashboard.day.saturday"),
      t("dashboard.day.sunday"),
    ],
    [t],
  )

  return (
    <Card
      {...uiSurface("UI-DLV2-074")}
      data-testid="dashboard-workload-card"
      variant="outlined"
      sx={{ flex: 1, borderRadius: 2 }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {t("dashboard.workload")}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 1.5 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t("dashboard.chart")}</InputLabel>
            <Select
              data-testid="dashboard-workload-mode-select"
              value={workloadMode}
              label={t("dashboard.chart")}
              onChange={(e) => setWorkloadMode(e.target.value)}
            >
              <MenuItem value="takeInCharge">
                {t("dashboard.workload.takeInCharge")}
              </MenuItem>
              <MenuItem value="callTime">
                {t("dashboard.workload.callTime")}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            data-testid="dashboard-workload-date-input"
            size="small"
            type="date"
            label={t("dashboard.weekOf")}
            value={workloadDate}
            onChange={(e) => setWorkloadDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            data-testid="dashboard-workload-display-btn"
            variant="contained"
            size="small"
          >
            {t("dashboard.display")}
          </Button>
        </Stack>

        <Box sx={chartPlaceholderSx}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            sx={{ height: "100%", px: 2, py: 2 }}
          >
            {[45, 58, 62, 54, 72, 38, 49].map((height, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  height: `${height}%`,
                  bgcolor: "primary.light",
                  opacity: 0.75,
                }}
              />
            ))}
          </Stack>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ px: 0.5, mt: 1 }}
        >
          {workloadDays.map((day) => (
            <Typography key={day} variant="caption" color="text.secondary">
              {day}
            </Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}
